import { useState, useEffect } from "react"
import axios from "axios"
import { Prompt, Params } from "../components/KmControls"
import { useLocalStorage } from "src/utils/useLocalStorage"

interface UsePromptsProps {
  generate?: boolean
  storageKey?: string
}

export const usePrompts = ({ generate, storageKey = "selectedPrompt" }: UsePromptsProps) => {
  const [prompts, setPrompts] = useState<Prompt[]>([])

  // Validate stored prompt against available prompts
  const isPromptValid = (storedPrompt: Prompt | undefined): boolean => {
    if (!storedPrompt) return false
    // If prompts haven't been fetched yet, keep the stored value
    if (prompts.length === 0) return true
    // Once prompts are fetched, validate against the list
    return prompts.some(
      (prompt) => prompt.title === storedPrompt.title && prompt.prompt === storedPrompt.prompt
    )
  }

  const [storedPrompt, setStoredPrompt] = useLocalStorage<Prompt | undefined>(
    storageKey,
    undefined,
    isPromptValid,
    [prompts]
  )

  const [params, setParams] = useState<Params>({
    prompts: [],
    selectedPrompt: storedPrompt || { title: "", prompt: "" }
  })

  useEffect(() => {
    if (generate) {
      axios
        .get(`/api/km/gen_prompts`)
        .then((response) => response.data)
        .then((data) => {
          const fetchedPrompts: Prompt[] = Object.values(data) as Prompt[]
          setPrompts(fetchedPrompts)

          // Determine selected prompt: use validated stored prompt or default to first
          const selectedPrompt = storedPrompt || fetchedPrompts[0]

          setParams((prevParams) => ({
            ...prevParams,
            prompts: fetchedPrompts,
            selectedPrompt: selectedPrompt
          }))
        })
    }
  }, [generate])

  // Wrap setParams to also persist selectedPrompt changes
  const setParamsWithStorage = (newParams: Params | ((prev: Params) => Params)) => {
    const updatedParams = typeof newParams === "function" ? newParams(params) : newParams
    setParams(updatedParams)
    if (updatedParams.selectedPrompt) {
      setStoredPrompt(updatedParams.selectedPrompt)
    }
  }

  return {
    params,
    setParams: setParamsWithStorage
  }
}
